import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { alphaApi as api } from './alpha-request';
import { ErrorState, Loading } from '../ui/State';

type Snapshot = {
  label: string;
  total: string;
  reserve: string;
  version: number;
  period?: { starts_on: string; ends_on: string };
  lines: { description: string; amount: string }[];
};
type Item = {
  id: string;
  snapshot: Snapshot;
  previous: Snapshot | null;
  current: boolean;
  version: number;
  status: string;
  dueOn: string;
  canRespond: boolean;
  response: { decision: string; reason: string } | null;
};
export function BudgetPortalAlpha() {
  const [items, setItems] = useState<Item[]>(),
    [error, setError] = useState<unknown>();
  const load = () => api<{ items: Item[] }>('/api/portal/budgets').then((r) => setItems(r.items));
  useEffect(() => {
    void load().catch(setError);
  }, []);
  return (
    <>
      <h1>Budget decisions</h1>
      <p>
        Review the retained figures. Approval applies only to this version and does not issue
        demands or release payments.
      </p>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {!items && !error ? (
        <Loading />
      ) : !items?.length ? (
        <p>No budgets are shared with you.</p>
      ) : (
        items.map((i) => <BudgetCard key={i.id} item={i} reload={load} />)
      )}
    </>
  );
}
function Figures({ value }: { value: Snapshot }) {
  return (
    <>
      <p>{value.period ? `${value.period.starts_on} to ${value.period.ends_on}` : ''}</p>
      <dl>
        {value.lines.map((l, i) => (
          <div key={i}>
            <dt>{l.description}</dt>
            <dd>£{l.amount}</dd>
          </div>
        ))}
      </dl>
      <p>
        <strong>Total £{value.total}</strong> · Reserve contribution £{value.reserve}
      </p>
    </>
  );
}
function BudgetCard({ item: i, reload }: { item: Item; reload: () => Promise<void> }) {
  const [decision, setDecision] = useState('Approved'),
    [reason, setReason] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(),
    [saved, setSaved] = useState(false),
    pending = useRef<Record<string, unknown> | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    pending.current ??= {
      decision,
      reason,
      expectedVersion: i.version,
      idempotencyKey: crypto.randomUUID(),
    };
    try {
      await api(`/api/portal/budgets/${i.id}/respond`, {
        method: 'POST',
        body: JSON.stringify(pending.current),
      });
      pending.current = null;
      setSaved(true);
      await reload();
    } catch (e) {
      if (
        (e as { statusCode?: number }).statusCode &&
        [400, 409, 422].includes((e as { statusCode: number }).statusCode)
      )
        pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel">
      <h2>{i.snapshot.label}</h2>
      <p>
        Version {i.snapshot.version} · Respond by {i.dueOn}
      </p>
      <Figures value={i.snapshot} />
      {i.previous && (
        <details>
          <summary>Compare with the previous shared version</summary>
          <Figures value={i.previous} />
        </details>
      )}
      {!i.current && (
        <p role="status">
          The budget or authority has changed. Your manager must share a new approval request.
        </p>
      )}
      {i.response && (
        <p>
          <strong>{i.response.decision}</strong>: {i.response.reason}
        </p>
      )}
      {saved && <p role="status">Your response is saved.</p>}
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {i.canRespond && (
        <form className="form" onSubmit={save}>
          <label>
            Response
            <select
              value={decision}
              disabled={busy || !!pending.current}
              onChange={(e) => setDecision(e.target.value)}
            >
              <option>Approved</option>
              <option>Rejected</option>
              <option>Changes requested</option>
            </select>
          </label>
          <label>
            Reason, questions or requested changes
            <textarea
              required
              minLength={5}
              maxLength={2000}
              value={reason}
              disabled={busy || !!pending.current}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button disabled={busy}>
            {busy ? 'Saving…' : pending.current ? 'Retry response' : 'Save response'}
          </button>
        </form>
      )}
    </section>
  );
}
export function BudgetPublicationAlpha() {
  const [q] = useSearchParams(),
    building = q.get('building'),
    [items, setItems] = useState<any[]>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState(false);
  const load = () =>
    api<{ items: any[] }>(`/api/alpha/budget-publications?building=${building}`).then((r) =>
      setItems(r.items),
    );
  useEffect(() => {
    if (building) void load().catch(setError);
  }, [building]);
  async function publish(i: any, s: any, u: any) {
    setBusy(true);
    setError(undefined);
    try {
      await api(`/api/alpha/budget-publications/${i.id}/publish`, {
        method: 'POST',
        body: JSON.stringify({
          stage: s.index,
          recipientUserId: u.id,
          expectedVersion: i.version,
          idempotencyKey: crypto.randomUUID(),
        }),
      });
      setSaved(true);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Share budget approvals</h1>
      <p>
        Prepare the approval sequence in Finance, then share the figures with each named approver.
      </p>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {saved && <p role="status">Budget shared in the portal. No email was sent.</p>}
      {!building ? (
        <p>Open this page from a building.</p>
      ) : !items && !error ? (
        <Loading />
      ) : !items?.length ? (
        <p>No pending client approval sequences.</p>
      ) : (
        items.map((i) => (
          <section className="panel" key={i.id}>
            <h2>{i.label}</h2>
            {i.stages.map((s: any) => (
              <div key={s.index}>
                <h3>
                  Stage {s.index + 1}: {s.name}
                </h3>
                {s.publication ? (
                  <p>
                    {s.publication.revoked_at
                      ? 'Withdrawn. Prepare a new approval sequence before sharing again.'
                      : 'Shared with the named approver.'}
                  </p>
                ) : s.recipients.length ? (
                  s.recipients.map((u: any) => (
                    <button disabled={busy} key={u.id} onClick={() => void publish(i, s, u)}>
                      Share with {u.name}
                    </button>
                  ))
                ) : (
                  <p>The named person needs director portal access.</p>
                )}
              </div>
            ))}
          </section>
        ))
      )}
    </>
  );
}
